import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import {
  ArrowLeft, Download, FileSpreadsheet, FileText, Search, Users,
  DollarSign, Clock, RefreshCw, Filter, Calendar, X, File, FileType, FileJson, BookOpen, Building,
  BarChart3, TrendingUp, AlertTriangle, Shield, Printer, Check, ChevronsUpDown
} from "lucide-react";
import { useAuth } from "../../App";
import { toast } from "sonner";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { fmtBs, COLORS } from "./reportesUtils";

applyPlugin(jsPDF);

const MorosidadInmobiliaria = () => {
  const navigate = useNavigate();
  const { API, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(false);
  const [inmLaeData, setInmLaeData] = useState(null);
  const [inmLaeAniosMin, setInmLaeAniosMin] = useState("");
  const [inmLaeUso, setInmLaeUso] = useState("todos");
  const [inmLaeCatInm, setInmLaeCatInm] = useState("todas");
  const [inmLaeFilters, setInmLaeFilters] = useState({ rif: "", nombre: "", codigo: "", propietario: "", uso: "", categoria: "", solvencia: "", anios: "", monto: "" });

  useEffect(() => { fetchInmobiliariaLaeData(); }, []);

  const fetchInmobiliariaLaeData = async (aplicarFiltros = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (inmLaeAniosMin) params.append("anios_mora_min", inmLaeAniosMin);
      if (inmLaeUso !== "todos") params.append("uso_propiedad", inmLaeUso);
      if (inmLaeCatInm !== "todas") params.append("categoria_inmueble", inmLaeCatInm);
      const url = `${API}/reportes/morosidad-inmobiliaria-lae?${params.toString()}`;
      const response = await axios.get(url, { headers });
      setInmLaeData(response.data);
      if (aplicarFiltros) toast.success("Filtros aplicados correctamente");
    } catch (error) {
      toast.error("Error al cargar datos de morosidad inmobiliaria");
    } finally {
      setLoading(false);
    }
  };

  const limpiarFiltrosInmLae = () => {
    setInmLaeAniosMin("");
    setInmLaeUso("todos");
    setInmLaeCatInm("todas");
    setInmLaeFilters({ rif: "", nombre: "", codigo: "", propietario: "", uso: "", categoria: "", solvencia: "", anios: "", monto: "" });
    setTimeout(() => fetchInmobiliariaLaeData(false), 0);
    toast.info("Filtros eliminados");
  };

  const hayFiltrosInmLaeActivos = inmLaeAniosMin || inmLaeUso !== "todos" || inmLaeCatInm !== "todas";
  const updateInmLaeFilter = (key, value) => setInmLaeFilters(prev => ({ ...prev, [key]: value }));
  const hayFiltrosInmLaeColActivos = Object.values(inmLaeFilters).some(v => v !== "");

  const filteredInmLaeFilas = inmLaeData?.filas?.filter(f => {
    const pf = inmLaeFilters;
    if (pf.rif && !(f.rif || "").toLowerCase().includes(pf.rif.toLowerCase())) return false;
    if (pf.nombre && !(f.nombre_contribuyente_lae || "").toLowerCase().includes(pf.nombre.toLowerCase())) return false;
    if (pf.codigo && !(f.codigo_catastral || "").toLowerCase().includes(pf.codigo.toLowerCase())) return false;
    if (pf.propietario && !(f.propietario_inmueble || "").toLowerCase().includes(pf.propietario.toLowerCase())) return false;
    if (pf.uso && !(f.uso_propiedad || "").toLowerCase().includes(pf.uso.toLowerCase())) return false;
    if (pf.categoria && !(f.categoria_inmueble || "").toLowerCase().includes(pf.categoria.toLowerCase())) return false;
    if (pf.solvencia && !String(f.ultima_solvencia || "").includes(pf.solvencia)) return false;
    if (pf.anios && !String(f.anios_morosos || 0).includes(pf.anios)) return false;
    if (pf.monto && !String(f.monto_adeudado || 0).includes(pf.monto)) return false;
    return true;
  }) || [];

  const inmLaeTotalesFiltrados = filteredInmLaeFilas.reduce((acc, f) => ({
    anios_morosos: acc.anios_morosos + (f.anios_morosos || 0),
    monto_adeudado: acc.monto_adeudado + (f.monto_adeudado || 0),
  }), { anios_morosos: 0, monto_adeudado: 0 });

  const exportInmLaeToExcel = () => {
    if (!inmLaeData) return;
    const wsData = [
      ["MOROSIDAD PROPIEDAD INMOBILIARIA CON LAE"], [],
      ["RIF", "CONTRIBUYENTE LAE", "CÓD. CATASTRAL", "PROPIETARIO INMUEBLE", "USO PROPIEDAD", "CATEGORÍA", "ÚLT. SOLVENCIA", "AÑOS MOROSOS", "MONTO ADEUDADO"]
    ];
    filteredInmLaeFilas.forEach(f => {
      wsData.push([f.rif, f.nombre_contribuyente_lae, f.codigo_catastral, f.propietario_inmueble, f.uso_propiedad, f.categoria_inmueble, f.ultima_solvencia, f.anios_morosos, f.monto_adeudado]);
    });
    wsData.push([]);
    wsData.push(["", "", "", "", "", "", "TOTALES", filteredInmLaeFilas.length + " registros", inmLaeTotalesFiltrados.monto_adeudado]);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "InmobiliariaLAE");
    XLSX.writeFile(wb, `Morosidad_Inmobiliaria_LAE_${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Exportado a Excel");
  };

  const exportInmLaeToPDF = () => {
    if (!inmLaeData) return;
    const doc = new jsPDF("l", "mm", "a4");
    doc.setFontSize(14);
    doc.setTextColor(79, 70, 229);
    doc.text("MOROSIDAD PROPIEDAD INMOBILIARIA CON LAE", 14, 18);
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Total registros morosos: ${filteredInmLaeFilas.length}`, 14, 25);
    const headCols = ["RIF", "Contribuyente LAE", "Cód. Catastral", "Propietario", "Uso", "Categoría", "Últ. Solv.", "Años Mor.", "Monto Adeudado"];
    const body = filteredInmLaeFilas.map(f => [f.rif, f.nombre_contribuyente_lae, f.codigo_catastral, f.propietario_inmueble, f.uso_propiedad, f.categoria_inmueble, f.ultima_solvencia, f.anios_morosos, fmtBs(f.monto_adeudado)]);
    body.push(["", "", "", "", "", "", "TOTALES", filteredInmLaeFilas.length + " reg.", fmtBs(inmLaeTotalesFiltrados.monto_adeudado)]);
    doc.autoTable({
      startY: 32, head: [headCols], body,
      styles: { fontSize: 6, cellPadding: 2 },
      headStyles: { fillColor: [79, 70, 229], textColor: 255 },
      alternateRowStyles: { fillColor: [238, 242, 255] },
      didParseCell: (data) => { if (data.row.index === body.length - 1) { data.cell.styles.fontStyle = "bold"; data.cell.styles.fillColor = [199, 210, 254]; } }
    });
    doc.save(`Morosidad_Inmobiliaria_LAE_${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("Exportado a PDF");
  };

  const exportInmLaeToCSV = () => {
    if (!inmLaeData) return;
    const cols = ["RIF", "CONTRIBUYENTE LAE", "CÓD. CATASTRAL", "PROPIETARIO INMUEBLE", "USO PROPIEDAD", "CATEGORÍA", "ÚLT. SOLVENCIA", "AÑOS MOROSOS", "MONTO ADEUDADO"];
    const rows = filteredInmLaeFilas.map(f => [`"${f.rif}"`, `"${f.nombre_contribuyente_lae}"`, `"${f.codigo_catastral}"`, `"${f.propietario_inmueble}"`, `"${f.uso_propiedad}"`, `"${f.categoria_inmueble}"`, `"${f.ultima_solvencia}"`, f.anios_morosos, f.monto_adeudado].join(","));
    const blob = new Blob(["\ufeff" + cols.join(",") + "\n" + rows.join("\n")], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `Morosidad_Inmobiliaria_LAE_${new Date().toISOString().split("T")[0]}.csv`);
    toast.success("Exportado a CSV");
  };

  const usosDisponibles = inmLaeData?.resumen?.usos_propiedad || [];
  const categoriasDisponibles = inmLaeData?.resumen?.categorias_inmueble || [];
  return (
    <div className="space-y-6" data-testid="reporte-morosidad-inmobiliaria-lae">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/reportes")} data-testid="inmlae-btn-volver">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Morosidad Propiedad Inmobiliaria con LAE
            </h1>
            <p className="text-slate-500">Propiedades de contribuyentes con LAE con solvencia pendiente</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => fetchInmobiliariaLaeData(false)} disabled={loading} data-testid="inmlae-btn-actualizar">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={!inmLaeData} data-testid="inmlae-btn-exportar">
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportInmLaeToExcel} data-testid="inmlae-export-excel">
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportInmLaeToPDF} data-testid="inmlae-export-pdf">
                <FileText className="w-4 h-4 mr-2" /> PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportInmLaeToCSV} data-testid="inmlae-export-csv">
                <File className="w-4 h-4 mr-2" /> CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filtros superiores */}
      <Card className="border-indigo-200 bg-indigo-50/30">
        <CardContent className="p-4">
          <div className="flex items-end gap-4 flex-wrap">
            <div className="w-[160px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Años Morosos Mín.</Label>
              <Input type="number" min="1" value={inmLaeAniosMin} onChange={(e) => setInmLaeAniosMin(e.target.value)} className="bg-white" placeholder="Ej: 2" data-testid="inmlae-anios-min" />
            </div>
            <div className="w-[200px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Uso Propiedad</Label>
              <Select value={inmLaeUso} onValueChange={setInmLaeUso}>
                <SelectTrigger className="bg-white" data-testid="inmlae-uso">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {usosDisponibles.map(u => (
                    <SelectItem key={u} value={u}>{u}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[200px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Categoría Inmueble</Label>
              <Select value={inmLaeCatInm} onValueChange={setInmLaeCatInm}>
                <SelectTrigger className="bg-white" data-testid="inmlae-cat-inmueble">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas</SelectItem>
                  {categoriasDisponibles.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => fetchInmobiliariaLaeData(true)} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="inmlae-btn-aplicar">
                <Search className="w-4 h-4 mr-2" /> Aplicar
              </Button>
              {hayFiltrosInmLaeActivos && (
                <Button variant="outline" onClick={limpiarFiltrosInmLae} disabled={loading} data-testid="inmlae-btn-limpiar">
                  <X className="w-4 h-4 mr-2" /> Limpiar
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-indigo-400" />
        </div>
      ) : inmLaeData ? (
        <>
          {/* Tarjetas resumen */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Contribuyentes Morosos</p>
                <p className="text-2xl font-bold text-indigo-700" data-testid="inmlae-card-contribuyentes">{inmLaeData.resumen?.total_contribuyentes_morosos || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Registros Morosos</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="inmlae-card-registros">{inmLaeData.resumen?.total_registros_morosos || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Código Pendiente</p>
                <p className="text-2xl font-bold text-yellow-600" data-testid="inmlae-card-pendientes">{inmLaeData.resumen?.total_codigo_pendiente || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Monto Total Adeudado</p>
                <p className="text-lg font-bold text-red-600" data-testid="inmlae-card-monto">{fmtBs(inmLaeData.resumen?.monto_total_adeudado)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Prom. Años Morosos</p>
                <p className="text-2xl font-bold text-amber-600" data-testid="inmlae-card-promedio">{inmLaeData.resumen?.promedio_anios_morosos || 0}</p>
              </CardContent>
            </Card>
          </div>

          {/* Gráficos */}
          {inmLaeData.filas?.length > 0 && (() => {
            const filas = inmLaeData.filas;
            // Distribución por Estado de Código
            const estadoCodigo = {};
            filas.forEach(f => { const e = f.estado_codigo || "Sin dato"; estadoCodigo[e] = (estadoCodigo[e] || 0) + 1; });
            const dataPieEstado = Object.entries(estadoCodigo).map(([nombre, cantidad]) => ({ nombre, cantidad }));
            
            // Distribución por Uso de Propiedad
            const usoProp = {};
            filas.forEach(f => { const u = f.uso_propiedad || "-"; if (u !== "-") usoProp[u] = (usoProp[u] || 0) + 1; });
            const dataPieUso = Object.entries(usoProp).sort((a,b) => b[1]-a[1]).map(([nombre, cantidad]) => ({ nombre, cantidad }));

            // Distribución por Años Morosos (agrupados)
            const aniosGroup = { "1 año": 0, "2-3 años": 0, "4-5 años": 0, "6-10 años": 0, "+10 años": 0, "Sin registro": 0 };
            filas.forEach(f => {
              const a = f.anios_morosos || 0;
              if (a === 0) aniosGroup["Sin registro"]++;
              else if (a === 1) aniosGroup["1 año"]++;
              else if (a <= 3) aniosGroup["2-3 años"]++;
              else if (a <= 5) aniosGroup["4-5 años"]++;
              else if (a <= 10) aniosGroup["6-10 años"]++;
              else aniosGroup["+10 años"]++;
            });
            const dataBarAnios = Object.entries(aniosGroup).filter(([,v]) => v > 0).map(([nombre, cantidad]) => ({ nombre, cantidad }));

            // Top 10 con mayor monto adeudado
            const topMontos = [...filas].filter(f => f.monto_adeudado > 0).sort((a,b) => b.monto_adeudado - a.monto_adeudado).slice(0, 10)
              .map(f => ({ nombre: (f.nombre_contribuyente_lae || "").substring(0, 25), monto: f.monto_adeudado }));

            const pieColors = ["#6366f1", "#818cf8", "#a5b4fc", "#c7d2fe", "#e0e7ff", "#4f46e5", "#4338ca", "#3730a3"];
            const pieColorsUso = ["#f59e0b", "#fbbf24", "#fcd34d", "#fde68a", "#d97706", "#b45309", "#92400e", "#78350f"];

            return (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Pie: Estado de Código */}
                <Card>
                  <CardContent className="p-4">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">Estado del Código Catastral</h3>
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie data={dataPieEstado} cx="50%" cy="50%" outerRadius={90} dataKey="cantidad" nameKey="nombre"
                          label={({ nombre, percent }) => `${nombre} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                          {dataPieEstado.map((_, i) => (
                            <Cell key={i} fill={i === 0 ? "#6366f1" : i === 1 ? "#fbbf24" : pieColors[i % pieColors.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Barras: Años morosos agrupados */}
                <Card>
                  <CardContent className="p-4">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">Distribución por Años de Mora</h3>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={dataBarAnios}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="nombre" tick={{ fontSize: 11 }} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="cantidad" name="Contribuyentes" fill="#6366f1" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Pie: Uso de Propiedad */}
                {dataPieUso.length > 0 && (
                  <Card>
                    <CardContent className="p-4">
                      <h3 className="text-sm font-semibold text-slate-700 mb-3">Distribución por Uso de Propiedad</h3>
                      <ResponsiveContainer width="100%" height={260}>
                        <PieChart>
                          <Pie data={dataPieUso} cx="50%" cy="50%" outerRadius={90} dataKey="cantidad" nameKey="nombre"
                            label={({ nombre, percent }) => `${(nombre || "").substring(0, 15)} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                            {dataPieUso.map((_, i) => (
                              <Cell key={i} fill={pieColorsUso[i % pieColorsUso.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {/* Barras: Top contribuyentes con mayor deuda */}
                {topMontos.length > 0 && (
                  <Card>
                    <CardContent className="p-4">
                      <h3 className="text-sm font-semibold text-slate-700 mb-3">Top Contribuyentes con Mayor Deuda</h3>
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={topMontos} layout="vertical" margin={{ left: 10, right: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" tickFormatter={(v) => `Bs.${(v/1000).toFixed(0)}k`} />
                          <YAxis dataKey="nombre" type="category" width={130} tick={{ fontSize: 10 }} />
                          <Tooltip formatter={(v) => fmtBs(v)} />
                          <Bar dataKey="monto" name="Monto Adeudado" fill="#ef4444" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </div>
            );
          })()}

          {/* Tabla */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Detalle de Morosidad Inmobiliaria</CardTitle>
                  <CardDescription>{filteredInmLaeFilas.length} de {inmLaeData.filas?.length || 0} registros</CardDescription>
                </div>
                {hayFiltrosInmLaeColActivos && (
                  <Button variant="outline" size="sm" onClick={() => setInmLaeFilters({ rif: "", nombre: "", codigo: "", propietario: "", uso: "", categoria: "", solvencia: "", anios: "", monto: "" })} data-testid="inmlae-limpiar-filtros-col">
                    <X className="w-4 h-4 mr-1" /> Limpiar filtros
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="inmlae-tabla">
                  <thead>
                    <tr className="border-b-2 border-indigo-200 bg-indigo-50">
                      <th className="text-left py-3 px-3 text-xs font-semibold text-indigo-800 uppercase">RIF</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-indigo-800 uppercase">Contribuyente LAE</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-indigo-800 uppercase">Cód. Catastral</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-indigo-800 uppercase">Propietario Inmueble</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-indigo-800 uppercase">Uso</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-indigo-800 uppercase">Categoría</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-indigo-800 uppercase">Últ. Solvencia</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-indigo-800 uppercase">Años Morosos</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-indigo-800 uppercase">Monto Adeudado</th>
                    </tr>
                    <tr className="bg-indigo-50/60 border-b border-indigo-100">
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={inmLaeFilters.rif} onChange={(e) => updateInmLaeFilter("rif", e.target.value)} data-testid="inmlae-filter-rif" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={inmLaeFilters.nombre} onChange={(e) => updateInmLaeFilter("nombre", e.target.value)} data-testid="inmlae-filter-nombre" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={inmLaeFilters.codigo} onChange={(e) => updateInmLaeFilter("codigo", e.target.value)} data-testid="inmlae-filter-codigo" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={inmLaeFilters.propietario} onChange={(e) => updateInmLaeFilter("propietario", e.target.value)} data-testid="inmlae-filter-propietario" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={inmLaeFilters.uso} onChange={(e) => updateInmLaeFilter("uso", e.target.value)} data-testid="inmlae-filter-uso" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={inmLaeFilters.categoria} onChange={(e) => updateInmLaeFilter("categoria", e.target.value)} data-testid="inmlae-filter-categoria" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={inmLaeFilters.solvencia} onChange={(e) => updateInmLaeFilter("solvencia", e.target.value)} data-testid="inmlae-filter-solvencia" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={inmLaeFilters.anios} onChange={(e) => updateInmLaeFilter("anios", e.target.value)} data-testid="inmlae-filter-anios" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={inmLaeFilters.monto} onChange={(e) => updateInmLaeFilter("monto", e.target.value)} data-testid="inmlae-filter-monto" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInmLaeFilas.length === 0 ? (
                      <tr><td colSpan={9} className="text-center py-12 text-slate-400">No se encontraron registros morosos</td></tr>
                    ) : (
                      filteredInmLaeFilas.map((f, idx) => (
                        <tr key={idx} className="border-b border-slate-100 hover:bg-indigo-50/30 transition-colors">
                          <td className="py-2.5 px-3 text-sm font-mono text-slate-700">{f.rif}</td>
                          <td className="py-2.5 px-3 text-sm text-slate-800">{f.nombre_contribuyente_lae}</td>
                          <td className="py-2.5 px-3 text-sm">
                            {f.estado_codigo === "Pendiente" ? (
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">Pendiente</span>
                            ) : (
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800 font-mono">{f.codigo_catastral}</span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-sm text-slate-700">{f.propietario_inmueble !== "-" ? f.propietario_inmueble : <span className="text-slate-400">-</span>}</td>
                          <td className="py-2.5 px-3 text-sm">
                            {f.uso_propiedad !== "-" ? (
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">{f.uso_propiedad}</span>
                            ) : <span className="text-slate-400">-</span>}
                          </td>
                          <td className="py-2.5 px-3 text-sm">
                            {f.categoria_inmueble !== "-" ? (
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-800">{f.categoria_inmueble}</span>
                            ) : <span className="text-slate-400">-</span>}
                          </td>
                          <td className="py-2.5 px-3 text-sm text-slate-700">{f.ultima_solvencia !== "-" ? f.ultima_solvencia : <span className="text-slate-400">-</span>}</td>
                          <td className="py-2.5 px-3 text-sm text-right">
                            {f.anios_morosos > 0 ? (
                              <span className={`font-bold ${f.anios_morosos >= 4 ? 'text-red-600' : f.anios_morosos >= 2 ? 'text-orange-600' : 'text-amber-600'}`}>
                                {f.anios_morosos}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-sm text-right font-semibold text-red-700">{fmtBs(f.monto_adeudado)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {filteredInmLaeFilas.length > 0 && (
                    <tfoot>
                      <tr className="bg-indigo-100 border-t-2 border-indigo-300">
                        <td colSpan={7} className="py-3 px-3 text-sm font-bold text-indigo-900 uppercase">Totales ({filteredInmLaeFilas.length} registros)</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-indigo-900">Prom: {(inmLaeTotalesFiltrados.anios_morosos / Math.max(filteredInmLaeFilas.length, 1)).toFixed(1)}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-red-700">{fmtBs(inmLaeTotalesFiltrados.monto_adeudado)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No se pudieron cargar los datos del reporte</p>
            <Button variant="outline" onClick={() => fetchInmobiliariaLaeData()} className="mt-4">
              <RefreshCw className="w-4 h-4 mr-2" /> Reintentar
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );

};

export default MorosidadInmobiliaria;
